import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Chip } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../api';

const DocumentVaultTab = ({ activeMenu, showMsg }) => {
  const [vaultDocs, setVaultDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;

  const fetchVaultData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/documents/all-document-details');
      setVaultDocs(res.data || []);
    } catch (err) { showMsg("Failed to load vault data", "error"); }
    setLoading(false);
  };

  useEffect(() => { fetchVaultData(); }, []);

  const handleStatusChange = async (docId, status) => {
    try {
      await api.put(`/admin/documents/${docId}/review?status=${status}`);
      showMsg(`Document ${status.toLowerCase()} successfully!`);
      fetchVaultData();
    } catch (err) { showMsg("Failed to update status", "error"); }
  };

  const renderStackedCell = (primary, secondary) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{primary || '--'}</Typography>
      <Typography variant="caption" color="text.secondary">{secondary || '--'}</Typography>
    </Box>
  );

  const renderCenterCell = (content) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{content}</Box>;

  const sharedCols = [
    { field: 'emp_details', headerName: 'Employee Name & ID', width: 220, valueGetter: (p, r) => `${r.employee_name} ${r.emp_id}`, renderCell: (p) => renderStackedCell(p.row.employee_name, p.row.emp_id) },
    { field: 'dept_details', headerName: 'Department & Designation', width: 220, valueGetter: (p, r) => `${r.department} ${r.designation}`, renderCell: (p) => renderStackedCell(p.row.department, p.row.designation) },
    { field: 'document_type', headerName: 'Document Type', width: 180, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.document_type}</Typography>) },
    { field: 'upload_date', headerName: 'Upload Date', width: 130, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.upload_date ? new Date(p.row.upload_date).toLocaleDateString() : '--'}</Typography>) },
  ];

  // SPLIT COLUMNS HERE
  const pendingApprovalCols = [
    ...sharedCols,
    { field: 'view', headerName: 'View', width: 90, sortable: false, renderCell: (p) => renderCenterCell(
        <IconButton size="small" color="info" onClick={() => window.open(`${baseUrl}/employees/documents/${p.row.document_id}/download`, '_blank')}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      )
    },
    { field: 'actions', headerName: 'Actions', width: 230, sortable: false, renderCell: (p) => renderCenterCell(
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" color="success" sx={{ minWidth: 0, px: 1 }} startIcon={<CheckCircleIcon />} disableElevation onClick={() => handleStatusChange(p.row.document_id, 'APPROVED')}>Approve</Button>
          <Button size="small" variant="contained" color="error" sx={{ minWidth: 0, px: 1 }} startIcon={<CancelIcon />} disableElevation onClick={() => handleStatusChange(p.row.document_id, 'REJECTED')}>Reject</Button>
        </Box>
      )
    }
  ];

const historyCols = [
    ...sharedCols,
    { field: 'status', headerName: 'Status', width: 120, renderCell: (p) => renderCenterCell(<Chip size="small" label={p.row.status} color={p.row.status === 'APPROVED' ? 'success' : 'error'} sx={{ fontWeight: 'bold', fontSize: '0.7rem' }} />) },
    
    // NEW: Action By column to show who approved or rejected the document
    { field: 'approved_by', headerName: 'Action By', width: 130, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.approved_by || '--'}</Typography>) },
    
    { field: 'action_date', headerName: 'Action Date', width: 110, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.action_date ? new Date(p.row.action_date).toLocaleDateString() : '--'}</Typography>) },
    { field: 'actions', headerName: 'View', width: 80, sortable: false, renderCell: (p) => renderCenterCell(
        <IconButton size="small" color="primary" onClick={() => window.open(`${baseUrl}/employees/documents/${p.row.document_id}/download`, '_blank')}>
          <VisibilityIcon />
        </IconButton>
      )
    }
  ];

  const data = activeMenu === 'pending_approval' ? vaultDocs.filter(d => d.status === 'PENDING') : vaultDocs.filter(d => d.status !== 'PENDING');

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <DataGrid
        loading={loading}
        rows={data}
        columns={activeMenu === 'pending_approval' ? pendingApprovalCols : historyCols}
        getRowId={(row) => row.document_id}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': { bgcolor: '#f5f5f5', fontWeight: 'bold' },
          '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type': { position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#fff', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.2)' }
        }}
      />
    </Box>
  );
};

export default DocumentVaultTab;